import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { DayPicker, type Matcher, useDayPicker, useNavigation } from "react-day-picker";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { ClickableIcon } from "./ClickableIcon";

const calendarVariants = cva(
	"w-fit h-fit rounded-md bg-surface-base px-[14px] pb-[10px] pt-[16px]",
	{
		variants: {
			elevation: {
				flat: "",
				raised: "shadow-lg",
			},
		},
		defaultVariants: { elevation: "raised" },
	},
);

const navControlClasses =
	"rounded-md px-[4px] py-[6px] hover:bg-surface-dark/40 hover:text-primary text-tertiary";

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const clampMonth = (month: Date, fromDate: Date | undefined, toDate: Date | undefined) => {
	if (fromDate && month < fromDate) return fromDate;
	if (toDate && month > toDate) return toDate;
	return month;
};

/**
 * Month/year stepper rendered in place of DayPicker's default caption. Reads navigation state
 * from context rather than props so the component identity stays stable across re-renders.
 */
const CalendarCaption = () => {
	const { currentMonth, goToMonth, nextMonth, previousMonth } = useNavigation();
	const { fromDate, toDate } = useDayPicker();

	const goToYear = (delta: number) =>
		goToMonth(
			clampMonth(
				new Date(currentMonth.getFullYear() + delta, currentMonth.getMonth(), 1),
				fromDate && startOfMonth(fromDate),
				toDate && startOfMonth(toDate),
			),
		);

	return (
		<div className="w-full min-h-fit flex items-center space-x-[10px] text-[15px] text-primary font-normal px-[12px] select-none mb-[14px]">
			<div className="flex w-[155px] h-full items-center space-x-[3px] py-[4px] px-[12px] rounded border border-bc-default bg-surface-light">
				<span className="flex-1 font-[450]">
					{currentMonth.toLocaleString(undefined, { month: "long" })}
				</span>
				<div className="h-full flex items-center">
					<ClickableIcon
						type="chevron-left"
						size="mini"
						tooltip="Previous month"
						className={navControlClasses}
						onClick={() => previousMonth && goToMonth(previousMonth)}
					/>
					<ClickableIcon
						type="chevron-right"
						size="mini"
						tooltip="Next month"
						className={navControlClasses}
						onClick={() => nextMonth && goToMonth(nextMonth)}
					/>
				</div>
			</div>
			<div className="flex flex-1 h-full items-center space-x-[3px] py-[4px] px-[8px] rounded border border-bc-default bg-surface-light">
				<span className="flex-1 font-[450]">{currentMonth.getFullYear()}</span>
				<div className="h-full flex items-center">
					<ClickableIcon
						type="chevron-left"
						size="mini"
						tooltip="Previous year"
						className={navControlClasses}
						onClick={() => goToYear(-1)}
					/>
					<ClickableIcon
						type="chevron-right"
						size="mini"
						tooltip="Next year"
						className={navControlClasses}
						onClick={() => goToYear(1)}
					/>
				</div>
			</div>
		</div>
	);
};

const dayPickerComponents = { Caption: CalendarCaption };

export type DatePickerProps = Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> &
	VariantProps<typeof calendarVariants> & {
		/** Currently selected day, or undefined when nothing is selected. */
		value?: Date;

		/** Month to open on when there is no selected day. */
		defaultMonth?: Date;

		/** Earliest navigable/selectable day. */
		fromDate?: Date;

		/** Latest navigable/selectable day. */
		toDate?: Date;

		/** Days that cannot be picked. */
		disabled?: Matcher[];

		/** Fires with the picked day, or undefined when the selection is cleared. */
		onSelect?: (value: Date | undefined) => void;

		/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
		trackingId?: string;

		ref?: Ref<HTMLDivElement>;
	};

/**
 * Single-date calendar primitive. Wraps `react-day-picker` with the portal's month/year stepper
 * caption and surface styling, and owns no popover, modal, or form state — the consumer decides
 * where it renders and what to do with the picked day.
 *
 * Reach for `form/inputs/DatePickerInput` when you need the button-plus-modal form control.
 */
export const DatePicker: FC<DatePickerProps> = ({
	value,
	defaultMonth,
	fromDate,
	toDate,
	disabled,
	onSelect,
	elevation,
	trackingId,
	className,
	ref,
	...rest
}) => (
	<div
		ref={ref}
		className={cn(calendarVariants({ elevation }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		<DayPicker
			selected={value}
			defaultMonth={value ?? defaultMonth}
			mode="single"
			captionLayout="dropdown-buttons"
			fromDate={fromDate}
			toDate={toDate}
			showOutsideDays
			fixedWeeks
			disabled={disabled}
			components={dayPickerComponents}
			onSelect={onSelect}
		/>
	</div>
);
DatePicker.displayName = "DatePicker";
